export type AmountValue = string | null | undefined;

interface ParsedAmount {
  negative: boolean;
  whole: string;
  fraction: string;
}

/** Formats a customer-facing summary without converting the decimal to Number. */
export function formatPrimarySol(value: AmountValue): string {
  const parsed = parseAmount(value);
  if (!parsed) return "--";

  const nonZero = parsed.whole !== "0" || /[1-9]/.test(parsed.fraction);
  const underCent = parsed.whole === "0" && parsed.fraction.padEnd(2, "0").slice(0, 2) === "00";
  if (nonZero && underCent) return `${parsed.negative ? "-" : ""}<0.01`;
  if (!nonZero) return "0.00";

  const fraction = parsed.fraction.padEnd(3, "0");
  let cents = BigInt(parsed.whole) * 100n + BigInt(fraction.slice(0, 2));
  if (fraction[2]! >= "5") cents += 1n;
  const whole = cents / 100n;
  const centsPart = String(cents % 100n).padStart(2, "0");
  return `${parsed.negative ? "-" : ""}${whole}.${centsPart}`;
}

export function formatSignedPrimarySol(value: AmountValue): string {
  const parsed = parseAmount(value);
  if (!parsed) return "--";
  if (parsed.whole === "0" && !/[1-9]/.test(parsed.fraction)) return "0.00";
  const unsigned = formatPrimarySol(
    parsed.fraction ? `${parsed.whole}.${parsed.fraction}` : parsed.whole,
  );
  return `${parsed.negative ? "-" : "+"}${unsigned}`;
}

/** Preserves the existing Demo detail precision while removing redundant zeroes. */
export function formatDemoDetailSol(value: AmountValue): string {
  const parsed = parseAmount(value);
  if (!parsed) return "--";
  let fraction = parsed.fraction.padEnd(4, "0").slice(0, 4);
  while (fraction.length > 2 && fraction.endsWith("0")) fraction = fraction.slice(0, -1);
  return `${parsed.negative ? "-" : ""}${parsed.whole}.${fraction}`;
}

/** Preserves the existing Live detail precision, including meaningful lamports. */
export function formatLiveDetailSol(value: AmountValue): string {
  const parsed = parseAmount(value);
  if (!parsed) return "--";
  let fraction = parsed.fraction.padEnd(4, "0").slice(0, 9);
  while (fraction.length > 4 && fraction.endsWith("0")) fraction = fraction.slice(0, -1);
  return `${parsed.negative ? "-" : ""}${parsed.whole}.${fraction}`;
}

function parseAmount(value: AmountValue): ParsedAmount | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return undefined;
  return {
    negative: match[1] === "-",
    whole: String(BigInt(match[2]!)),
    fraction: match[3] ?? "",
  };
}
